import type { TeamRole } from '@/api-models/teams';
import useSWR from 'swr';

import type { TeamBalance } from '@/api-models/balance';
import { fetchEndpointData } from '@/utils/fetch.client';

/** The balance fields are flattened onto the row by the backend. */
export interface AdminTeamRow extends TeamBalance {
  id: string;
  name: string;
  slug: string;
  is_personal: boolean;
  member_count: number;
  repository_count: number;
  workspace_count: number;
  created_at: string;
}

interface ListTeamsResponse {
  teams: AdminTeamRow[];
  total: number;
}

export interface AdminTeamsQuery {
  search?: string;
  page: number;
  pageSize: number;
}

function buildKey({ search, page, pageSize }: AdminTeamsQuery) {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  params.set('limit', String(pageSize));
  params.set('offset', String((page - 1) * pageSize));
  return `/api/v1/admin/teams?${params.toString()}`;
}

export function useAdminTeams(query: AdminTeamsQuery) {
  const key = buildKey(query);
  const { data, mutate, isLoading, error } = useSWR(key, () => fetchEndpointData<ListTeamsResponse>(key), {
    keepPreviousData: true,
  });
  return {
    teams: data?.teams ?? [],
    total: data?.total ?? 0,
    mutate,
    isLoading,
    error,
  };
}

export interface AdminTeamMember {
  user_id: string;
  email: string;
  name: string;
  role: TeamRole;
  joined_at: string;
}

export interface AdminTeamRepository {
  id: string;
  name: string;
  created_at: string;
}

export interface AdminTeamDetail extends TeamBalance {
  id: string;
  name: string;
  slug: string;
  is_personal: boolean;
  created_at: string;
  members: AdminTeamMember[];
  repositories: AdminTeamRepository[];
}

export function useAdminTeam(teamId: string | undefined) {
  const { data, mutate, isLoading, error } = useSWR(teamId ? `/api/v1/admin/teams/${teamId}` : null, () =>
    fetchEndpointData<AdminTeamDetail>(`/api/v1/admin/teams/${teamId}`),
  );
  return { team: data, mutate, isLoading, error };
}
